import { jsPDF } from "jspdf";

export interface SignatureInfo {
  signatureText: string;
  signatureDate: string;
}

export interface PDFDocumentOptions {
  title: string;
  subtitle?: string;
  patientName: string;
  content: string;
  filename: string;
  signature?: SignatureInfo;
}

// Simple markdown detection
const isMarkdown = (text: string): boolean => {
  const patterns = [
    /^#{1,6}\s+/m, // Headers
    /\*\*[^*]+\*\*/, // Bold text
    /^\s*[-*]\s+/m, // Bullet lists
    /^\s*\d+\.\s+/m, // Numbered lists
  ];

  return patterns.some((pattern) => pattern.test(text));
};

// Helper function to render text with mixed formatting
const renderFormattedText = (
  text: string,
  doc: jsPDF,
  x: number,
  y: number,
  maxWidth: number,
): number => {
  const parts: Array<{ text: string; bold: boolean; italic: boolean }> = [];
  let currentText = text;
  let currentPos = 0;

  // Parse bold and italic patterns
  while (currentPos < currentText.length) {
    // Look for bold patterns (**text**)
    const boldMatch = currentText.slice(currentPos).match(/\*\*([^*]+)\*\*/);
    // Look for italic patterns (*text*) - but not bold
    const italicMatch = currentText
      .slice(currentPos)
      .match(/(?<!\*)\*([^*\n]+)\*(?!\*)/);

    let nextMatch = null;
    let matchType = "";
    let matchStart = currentText.length;

    if (boldMatch && boldMatch.index !== undefined) {
      const start = currentPos + boldMatch.index;
      if (start < matchStart) {
        matchStart = start;
        nextMatch = boldMatch;
        matchType = "bold";
      }
    }

    if (italicMatch && italicMatch.index !== undefined) {
      const start = currentPos + italicMatch.index;
      if (start < matchStart) {
        matchStart = start;
        nextMatch = italicMatch;
        matchType = "italic";
      }
    }

    if (nextMatch) {
      // Add text before the match as regular text
      if (matchStart > currentPos) {
        const beforeText = currentText.slice(currentPos, matchStart);
        if (beforeText.trim()) {
          parts.push({ text: beforeText, bold: false, italic: false });
        }
      }

      // Add the matched text with formatting
      const matchedText = nextMatch[1];
      parts.push({
        text: matchedText,
        bold: matchType === "bold",
        italic: matchType === "italic",
      });

      // Move position past the match
      currentPos = matchStart + nextMatch[0].length;
    } else {
      // No more matches, add remaining text
      const remainingText = currentText.slice(currentPos);
      if (remainingText.trim()) {
        parts.push({ text: remainingText, bold: false, italic: false });
      }
      break;
    }
  }

  // If no formatting found, treat as regular text
  if (parts.length === 0) {
    parts.push({ text: currentText, bold: false, italic: false });
  }

  // Render the parts
  let currentX = x;
  let currentY = y;
  const lineHeight = 7;

  for (const part of parts) {
    // Set font based on formatting
    if (part.bold) {
      doc.setFont("helvetica", "bold");
    } else if (part.italic) {
      doc.setFont("helvetica", "italic");
    } else {
      doc.setFont("helvetica", "normal");
    }

    // Handle text wrapping for this part
    const wrappedLines = doc.splitTextToSize(
      part.text,
      maxWidth - (currentX - x),
    );

    for (let i = 0; i < wrappedLines.length; i++) {
      const line = wrappedLines[i];

      // Check if we need to wrap to next line
      const textWidth = doc.getTextWidth(line);
      if (currentX + textWidth > x + maxWidth && currentX > x) {
        currentX = x;
        currentY += lineHeight;
      }

      doc.text(line, currentX, currentY);
      currentX += textWidth;

      // If this was a wrapped line, move to next line
      if (i < wrappedLines.length - 1) {
        currentX = x;
        currentY += lineHeight;
      }
    }
  }

  return currentY;
};

// Convert markdown to formatted text for PDF
const parseMarkdownForPDF = (
  text: string,
  doc: jsPDF,
  margin: number,
  contentWidth: number,
  startY: number,
): number => {
  const lines = text.split("\n");
  let yPosition = startY;
  const lineHeight = 7;
  const pageHeight = doc.internal.pageSize.getHeight();

  for (const line of lines) {
    // Check if we need a new page
    if (yPosition + lineHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }

    const trimmedLine = line.trim();

    if (!trimmedLine) {
      // Empty line - add small space
      yPosition += lineHeight * 0.5;
      continue;
    }

    // Headers
    if (trimmedLine.match(/^#{1,6}\s+/)) {
      const headerText = trimmedLine.replace(/^#{1,6}\s+/, "");
      const headerLevel = (trimmedLine.match(/^#{1,6}/) || [""])[0].length;

      yPosition += lineHeight * 0.5; // Space before header

      if (headerLevel === 1) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
      } else if (headerLevel === 2) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
      }

      doc.text(headerText, margin, yPosition);
      yPosition += lineHeight + 2;
      continue;
    }

    // Bullet points
    if (trimmedLine.match(/^\s*[-*]\s+/)) {
      const bulletText = trimmedLine.replace(/^\s*[-*]\s+/, "");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      // Add bullet point
      doc.text("•", margin + 5, yPosition);

      // Handle bullet text with formatting
      const bulletY = renderFormattedText(
        bulletText,
        doc,
        margin + 15,
        yPosition,
        contentWidth - 15,
      );
      yPosition = bulletY + lineHeight;
      continue;
    }

    // Numbered lists
    const numberedMatch = trimmedLine.match(/^\s*(\d+)\.\s+(.+)/);
    if (numberedMatch) {
      const [, number, listText] = numberedMatch;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      // Add number
      doc.text(`${number}.`, margin + 5, yPosition);

      // Handle numbered text with formatting
      const listY = renderFormattedText(
        listText,
        doc,
        margin + 20,
        yPosition,
        contentWidth - 20,
      );
      yPosition = listY + lineHeight;
      continue;
    }

    // Regular paragraph - handle with formatting
    let processedLine = trimmedLine;

    // Normalize quotes and other characters that might cause issues
    processedLine = processedLine
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      .replace(/[–—]/g, "-")
      .replace(/≈/g, "~") // Replace approximately symbol
      .replace(/±/g, "+/-") // Replace plus-minus symbol
      .replace(/°/g, " degrees") // Replace degree symbol
      .replace(/×/g, "x") // Replace multiplication symbol
      .replace(/…/g, "...") // Replace ellipsis
      .normalize("NFKC");

    doc.setFontSize(11);

    // Render with formatting
    yPosition = renderFormattedText(
      processedLine,
      doc,
      margin,
      yPosition,
      contentWidth,
    );
    yPosition += lineHeight + 2; // Small space after paragraph
  }

  return yPosition;
};

// Add signature to PDF
const addSignature = (
  doc: jsPDF,
  signature: SignatureInfo,
  margin: number,
  currentY: number,
): void => {
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = currentY + 15;

  // Check if we need a new page for signature
  if (yPosition + 50 > pageHeight - margin) {
    doc.addPage();
    yPosition = margin + 15;
  }

  // Signature line
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition + 20, margin + 160, yPosition + 20);

  // Signature text
  doc.setFont("times", "italic");
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 100);
  doc.text(signature.signatureText, margin + 6, yPosition + 16);

  // Labels
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("Signature", margin, yPosition + 30);
  doc.setFontSize(11);
  doc.text(`Date: ${signature.signatureDate}`, margin, yPosition + 42);
};

export const generatePDFDocument = (options: PDFDocumentOptions): void => {
  const { title, subtitle, patientName, content, filename, signature } =
    options;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPosition = margin;

  // Add header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, margin, yPosition);
  yPosition += 12;

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(subtitle, margin, yPosition);
    yPosition += 10;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(`Patient: ${patientName}`, margin, yPosition);
  yPosition += 14;

  // Check if we need a new page before content
  if (yPosition + 20 > pageHeight - margin) {
    doc.addPage();
    yPosition = margin;
  }

  // Add "Summary:" label
  doc.setFont("helvetica", "bold");
  doc.text("Summary:", margin, yPosition);
  yPosition += 10;

  // Process content based on whether it's markdown or plain text
  if (isMarkdown(content)) {
    yPosition = parseMarkdownForPDF(
      content,
      doc,
      margin,
      contentWidth,
      yPosition,
    );
  } else {
    // Plain text handling
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    const splitContent = doc.splitTextToSize(content, contentWidth);
    const lineHeight = 7;

    for (let i = 0; i < splitContent.length; i++) {
      if (yPosition + lineHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
      doc.text(splitContent[i], margin, yPosition);
      yPosition += lineHeight;
    }
  }

  // Add signature if provided
  if (signature) {
    addSignature(doc, signature, margin, yPosition);
  }

  // Save the PDF
  doc.save(filename);
};

export const generatePatientNoteFilename = (
  patientName: string,
  noteType: string,
  date: string,
  suffix: string = "",
  extension: string = "pdf",
): string => {
  const cleanPatientName = patientName.replace(/\s+/g, "_");
  const cleanNoteType = noteType.replace(/\s+/g, "_");
  const cleanDate = date.replace(/\s+/g, "_").replace(/,/g, "");

  return `${cleanPatientName}_${cleanNoteType}_${cleanDate}${suffix ? `_${suffix}` : ""}.${extension}`;
};
